import { useState, useEffect, useCallback } from 'react';
import { X, Newspaper, ExternalLink, ChevronDown, ChevronUp, TriangleAlert as AlertTriangle, TrendingUp, ShieldAlert } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeUrl } from '../../lib/sanitizeUrl';
import type { NewsAlertResult, NewsAlertArticle } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface AlertWithAccount extends NewsAlertResult {
  company_name: string;
}

const IMPACT_ICONS: Record<string, typeof AlertTriangle> = {
  risk: ShieldAlert,
  revenue: TrendingUp,
  cost: AlertTriangle,
};

const IMPACT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  risk: { bg: 'bg-app-critical/10', text: 'text-app-critical', border: 'border-app-critical' },
  revenue: { bg: 'bg-app-positive/10', text: 'text-app-positive', border: 'border-app-positive' },
  cost: { bg: 'bg-app-warning/10', text: 'text-app-warning', border: 'border-app-warning' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function AlertArticleCard({ article, companyName }: { article: NewsAlertArticle; companyName: string }) {
  const [expanded, setExpanded] = useState(false);
  const impactType = article.risk_revenue_cost_impact?.type || 'risk';
  const colors = IMPACT_COLORS[impactType] || IMPACT_COLORS.risk;
  const ImpactIcon = IMPACT_ICONS[impactType] || AlertTriangle;

  return (
    <div className={`rounded-lg border ${colors.border} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full p-4 text-left ${colors.bg} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-start gap-3">
          <ImpactIcon className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">{companyName}</p>
            <p className="text-sm font-semibold text-app-ink leading-snug">{article.headline}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs text-app-muted">{article.source}</span>
              {article.published_at && (
                <span className="text-xs text-app-muted">{formatDate(article.published_at)}</span>
              )}
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-app-muted flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-app-muted flex-shrink-0" />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 bg-app-surface space-y-4">
          <div>
            <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">Opportunity</p>
            <p className="text-sm text-app-ink leading-relaxed">{article.opportunity_summary}</p>
          </div>

          <div className="grid grid-cols-1 gap-2">
            {article.technical_initiative_impacted && (
              <div className="p-2.5 bg-app-positive/10 rounded-lg">
                <p className="text-[10px] font-bold text-app-positive uppercase tracking-wider">Technical Initiative</p>
                <p className="text-xs text-app-ink mt-0.5">{article.technical_initiative_impacted}</p>
              </div>
            )}
            {article.business_strategy_impacted && (
              <div className="p-2.5 bg-app-focus/10 rounded-lg">
                <p className="text-[10px] font-bold text-app-focus uppercase tracking-wider">Business Strategy</p>
                <p className="text-xs text-app-ink mt-0.5">{article.business_strategy_impacted}</p>
              </div>
            )}
            {article.corporate_objective_impacted && (
              <div className="p-2.5 bg-app-warning/10 rounded-lg">
                <p className="text-[10px] font-bold text-app-warning uppercase tracking-wider">Corporate Objective</p>
                <p className="text-xs text-app-ink mt-0.5">{article.corporate_objective_impacted}</p>
              </div>
            )}
          </div>

          {article.risk_revenue_cost_impact && (
            <div>
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">Impact Assessment</p>
              <div className={`p-2.5 rounded-lg ${colors.bg}`}>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${colors.text}`}>{impactType}</span>
                <p className="text-xs text-app-ink mt-0.5">{article.risk_revenue_cost_impact.description}</p>
                <p className="text-xs text-app-muted mt-0.5">Magnitude: {article.risk_revenue_cost_impact.estimated_magnitude}</p>
              </div>
            </div>
          )}

          {article.affected_personas && article.affected_personas.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">Affected Personas</p>
              <div className="space-y-1">
                {article.affected_personas.map((p, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="font-medium text-app-ink">{p.title}:</span>
                    <span className="text-app-muted">{p.why_affected}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {article.time_sensitivity && (
            <div>
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">Time Sensitivity</p>
              <p className="text-xs text-app-ink">{article.time_sensitivity}</p>
            </div>
          )}

          {article.commercial_impact_estimate && (
            <div>
              <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-1">Commercial Impact</p>
              <p className="text-xs text-app-ink">{article.commercial_impact_estimate}</p>
            </div>
          )}

          {article.vendor_solution_relevance && (
            <div className="p-3 bg-forest-50 rounded-lg">
              <p className="text-[10px] font-bold text-forest-700 uppercase tracking-wider mb-1">How Our Solution Helps</p>
              <p className="text-xs text-app-ink leading-relaxed">{article.vendor_solution_relevance}</p>
            </div>
          )}

          {article.recommended_action && (
            <div className="p-3 bg-app-focus/10 rounded-lg">
              <p className="text-[10px] font-bold text-app-focus uppercase tracking-wider mb-1">Recommended Action</p>
              <p className="text-xs text-app-ink leading-relaxed">{article.recommended_action}</p>
            </div>
          )}

          {article.url && (
            <a
              href={sanitizeUrl(article.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-forest-600 hover:text-forest-700 font-medium"
            >
              <ExternalLink className="w-3 h-3" /> Read full article
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function NewsroomSidebar({ isOpen, onClose }: Props) {
  const { user, organisation } = useAuth();
  const { apiGet } = useApi();
  const [alerts, setAlerts] = useState<AlertWithAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(async () => {
    if (!user || !organisation) return;
    setLoading(true);

    const { alerts: fetched } = await apiGet<{ alerts: AlertWithAccount[]; count: number }>(
      `/v1/newsroom?org_id=${encodeURIComponent(organisation.id)}`,
    ).catch(() => ({ alerts: [] as AlertWithAccount[], count: 0 }));

    setAlerts(fetched);
    setLoading(false);
  }, [user, organisation, apiGet]);

  useEffect(() => {
    if (isOpen) loadAlerts();
  }, [isOpen, loadAlerts]);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-app-surface shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-app-line">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-app-critical/10 rounded-lg flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-app-critical" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-app-ink">Newsroom</h2>
              <p className="text-xs text-app-muted">Intelligence alerts across your accounts</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100%-88px)] p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-forest-200 border-t-forest-600 rounded-full animate-spin" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="w-12 h-12 text-app-line mx-auto mb-4" />
              <h3 className="text-base font-semibold text-app-muted mb-2">No alerts yet</h3>
              <p className="text-sm text-app-muted max-w-xs mx-auto">
                When high-priority news breaks for your subscribed accounts, alerts will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map(alert => (
                <div key={alert.id}>
                  <p className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-2">
                    Week of {formatDate(alert.alert_date)}
                  </p>
                  <div className="space-y-3">
                    {(alert.alert_detail || []).map((article, i) => (
                      <AlertArticleCard
                        key={`${alert.id}-${i}`}
                        article={article}
                        companyName={alert.company_name}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
